<template>
  <va-modal
    v-model="state.isOpen"
    hide-default-actions
    :title="$t(`${licensingObject}s.editOwnerModal.title`)"
    class="change-owner-modal"
  >
    <slot>
      <div class="bodyContainer va-gutter-3">
        <va-button-toggle
          v-if="state.isEntitlement"
          outline
          class="mb-4 full-width-toggle"
          v-model="state.entityType"
          :options="ownerTypeOptions"
        />

        <!-- User Section -->
        <div v-if="state.entityType === 'User'" class="entity-section">
          <va-divider class="section-divider">
            <span class="divider-text">
              <va-icon name="search" class="mr-1" />
              {{ $t("entitlements.editOwnerModal.searchUserLabel") }}
            </span>
          </va-divider>
          <searchable-dropdown
            class="mb-3"
            :label="$t('entitlements.editOwnerModal.searchUserPlaceholder')"
            :placeholder="
              $t('entitlements.editOwnerModal.searchUserPlaceholder')
            "
            :search-function="searchUsers"
            v-model="state.userID"
            entity-type="User"
          />

          <va-divider class="section-divider">
            <span class="divider-text">
              <va-icon name="input" class="mr-1" />
              {{ $t(`${licensingObject}s.editOwnerModal.userID`) }} (Direct)
            </span>
          </va-divider>
          <va-input
            class="mb-3 direct-id-input"
            v-model="state.userID"
            :disabled="state.isAnonymous"
            :placeholder="$t(`${licensingObject}s.editOwnerModal.theUserID`)"
            :error="
              state.userIDOk === false && !!state.userID && !state.isAnonymous
            "
            :success="
              state.userIDOk === true && !!state.userID && !state.isAnonymous
            "
          />

          <!-- Unified Preview for User -->
          <div
            v-if="state.userID && !state.isAnonymous"
            class="unified-preview-container mt-2"
          >
            <div
              v-if="state.userIDOk && state.userInfo"
              class="unified-preview success-preview pa-2"
            >
              <va-icon name="person" color="success" class="mr-2" />
              <div>
                <div>
                  <strong>{{ state.userInfo.displayName }}</strong>
                </div>
                <div class="text-secondary">{{ state.userInfo.email }}</div>
              </div>
            </div>
            <div
              v-if="state.userIDOk === false"
              class="unified-preview error-preview pa-2"
            >
              <va-icon name="error" color="danger" class="mr-2" />
              {{ $t("users.notFound") }}
            </div>
          </div>

          <template v-if="!state.isEntitlement">
            <div class="anonymous-section mt-3">
              <input
                id="anonymousLicenseUser"
                type="checkbox"
                v-model="state.isAnonymous"
              />
              <label class="ml-2" for="anonymousLicenseUser">
                {{ $t(`${licensingObject}s.editOwnerModal.anonymous`) }}
              </label>
            </div>
          </template>
        </div>

        <!-- Organization Section -->
        <div v-if="state.entityType === 'Organization'" class="entity-section">
          <va-divider class="section-divider">
            <span class="divider-text">
              <va-icon name="search" class="mr-1" />
              {{ $t("entitlements.editOwnerModal.searchOrgLabel") }}
            </span>
          </va-divider>
          <searchable-dropdown
            class="mb-3"
            :label="$t('entitlements.editOwnerModal.searchOrgPlaceholder')"
            :placeholder="
              $t('entitlements.editOwnerModal.searchOrgPlaceholder')
            "
            :search-function="searchOrganizations"
            v-model="state.organizationID"
            entity-type="Organization"
          />

          <va-divider class="section-divider">
            <span class="divider-text">
              <va-icon name="input" class="mr-1" />
              {{ $t(`${licensingObject}s.editOwnerModal.organizationID`) }}
              (Direct)
            </span>
          </va-divider>
          <va-input
            class="mb-3 direct-id-input"
            v-model="state.organizationID"
            :placeholder="
              $t(`${licensingObject}s.editOwnerModal.theOrganizationID`)
            "
            :error="state.organizationIDOk === false && !!state.organizationID"
            :success="state.organizationIDOk === true && !!state.organizationID"
          />

          <!-- Unified Preview for Organization -->
          <div
            v-if="state.organizationID"
            class="unified-preview-container mt-2"
          >
            <div
              v-if="state.organizationIDOk && state.organizationInfo"
              class="unified-preview success-preview pa-2"
            >
              <va-icon name="business" color="success" class="mr-2" />
              <div>
                <strong>{{ state.organizationInfo.name }}</strong>
              </div>
            </div>
            <div
              v-if="state.organizationIDOk === false"
              class="unified-preview error-preview pa-2"
            >
              <va-icon name="error" color="danger" class="mr-2" />
              {{ $t("organizations.notFound") }}
            </div>
          </div>
        </div>
      </div>
    </slot>
    <template #footer>
      <va-button flat outline class="mr-1" @click="closeAndCancel">
        {{ $t("buttons.cancel") }}
      </va-button>
      <va-button
        class="ml-1"
        :disabled="
          (state.entityType === 'User' &&
            !state.userIDOk &&
            !state.isAnonymous) ||
            (state.entityType === 'Organization' && !state.organizationIDOk)
        "
        color="primary"
        @click="applyChange()"
      >
        {{ $t("buttons.validate") }}
      </va-button>
    </template>
  </va-modal>
</template>

<script setup>
import { reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import {
  callFetchUser,
  callFetchOrganization,
  callFetchUsers,
  callFetchOrganizations
} from "@/api/identity";
import SearchableDropdown from "@/components/utils/SearchableDropdown.vue";
import { VaIcon } from "vuestic-ui";

const { t } = useI18n();
const store = useStore();

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  entityType: {
    type: String,
    default: "User"
  },
  licensingObject: {
    type: String,
    required: true
  },
  isAnonymous: {
    type: Boolean,
    default: false
  }
});

const state = reactive({
  isOpen: props.isOpen,
  isEntitlement: props.licensingObject === "entitlement",
  entityType: props.entityType,
  isAnonymous: props.isAnonymous,
  organizationID: "",
  organizationIDOk: undefined,
  organizationInfo: null,
  userID: "",
  userIDOk: undefined,
  userInfo: null
});

watch(
  () => state.isOpen,
  (newInternalOpenState, oldInternalOpenState) => {
    if (!newInternalOpenState && oldInternalOpenState) {
      emit("cancel");
    }
  }
);

watch(
  () => props.isOpen,
  newVal => {
    if (state.isOpen !== newVal) {
      state.isOpen = newVal;
    }
    if (newVal) {
      state.entityType = props.entityType;
      state.isAnonymous = props.isAnonymous;
      state.organizationID = "";
      state.organizationIDOk = undefined;
      state.organizationInfo = null;
      state.userID = "";
      state.userIDOk = undefined;
      state.userInfo = null;
    } else {
      state.organizationID = "";
      state.organizationIDOk = undefined;
      state.organizationInfo = null;
      state.userID = "";
      state.userIDOk = undefined;
      state.userInfo = null;
    }
  }
);

watch(
  () => state.userID,
  async newUserId => {
    state.userInfo = null;
    if (newUserId && state.entityType === "User") {
      try {
        const resp = await callFetchUser(newUserId);
        state.userInfo = resp?.data || null;
        state.userIDOk = !!state.userInfo;
      } catch {
        state.userInfo = null;
        state.userIDOk = false;
      }
    } else if (!newUserId && state.entityType === "User") {
      state.userIDOk = undefined;
    }
  }
);

watch(
  () => state.organizationID,
  async newOrgId => {
    state.organizationInfo = null;
    if (newOrgId && state.entityType === "Organization") {
      try {
        const resp = await callFetchOrganization(newOrgId);
        state.organizationInfo = resp?.data || null;
        state.organizationIDOk = !!state.organizationInfo;
      } catch {
        state.organizationInfo = null;
        state.organizationIDOk = false;
      }
    } else if (!newOrgId && state.entityType === "Organization") {
      state.organizationIDOk = undefined;
    }
  }
);

watch(
  () => state.entityType,
  newEntityType => {
    if (newEntityType === "User") {
      state.organizationID = "";
      state.organizationIDOk = undefined;
      state.organizationInfo = null;
    } else if (newEntityType === "Organization") {
      state.userID = "";
      state.userIDOk = undefined;
      state.userInfo = null;
    }
  }
);

const ownerTypeOptions = [
  { label: t("entityType.organization"), value: "Organization" },
  { label: t("entityType.user"), value: "User" }
];

const emit = defineEmits(["cancel", "validate"]);

const closeAndCancel = () => {
  emit("cancel");
};

const closeAndValidate = () => {
  emit("validate");
};

const applyChange = () => {
  switch (props.licensingObject) {
    case "assignment":
      changeAssignee();
      break;
    case "entitlement":
      changeOwner();
      break;
    default:
      break;
  }
};

const changeAssignee = async () => {
  await store.dispatch("licensing/updateAssignmentAssignee", {
    assignmentId: store.state.licensing.assignment.id,
    assigneeId:
      state.entityType === "Organization" ? state.organizationID : state.userID
  });
  closeAndValidate();
};

const changeOwner = async () => {
  const newOwnerId =
    state.entityType === "Organization" ? state.organizationID : state.userID;
  store.dispatch("licensing/updateEntitlementEditField", {
    key: "ownerId",
    value: newOwnerId
  });
  store.dispatch("licensing/updateEntitlementEditField", {
    key: "ownerType",
    value: state.entityType
  });
  closeAndValidate();
};

const searchUsers = async searchTerm => {
  if (!searchTerm || searchTerm.length < 3) {
    return [];
  }
  try {
    const response = await callFetchUsers({
      params: { Search: searchTerm, Count: 10 }
    });
    return response.data.items.map(user => ({
      label: `${user.displayName || "N/A"} (${user.email || "N/A"})`,
      value: user.id
    }));
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

const searchOrganizations = async searchTerm => {
  if (!searchTerm || searchTerm.length < 3) {
    return [];
  }
  try {
    const response = await callFetchOrganizations({
      params: { Search: searchTerm, Count: 10 }
    });
    return response.data.items.map(org => ({
      label: org.name || "N/A",
      value: org.id
    }));
  } catch (error) {
    console.error("Error fetching organizations:", error);
    return [];
  }
};
</script>

<style lang="scss" scoped>
.change-owner-modal .va-modal__container {
  min-width: 550px; /* Slightly increase the modal width */
}

.bodyContainer {
  display: flex;
  flex-direction: column;
  width: 40rem;
  gap: 1rem; /* Space between main sections */
}

.full-width-toggle {
  width: 100%;
}

.entity-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--va-background-border);
  border-radius: var(--va-card-border-radius);
  background-color: var(--va-background-secondary);
}

.section-divider .divider-text {
  display: flex;
  align-items: center;
  font-size: 0.9em;
  color: var(--va-text-secondary);
}

.section-divider .va-icon {
  font-size: 1.1em; /* Adjust icon size in the divider */
}

.direct-id-input .va-input-wrapper__label {
  /* If we want to hide the label because it's already in the divider */
  /* display: none; */
}

.unified-preview-container {
  /* Container for the preview */
}

.unified-preview {
  display: flex;
  align-items: center;
  border-radius: var(--va-button-border-radius);
  font-size: 0.95em;
}

.success-preview {
  background-color: var(--va-success-opacity-20, rgba(76, 175, 80, 0.1));
  border-left: 4px solid var(--va-success);
  color: var(--va-text-primary);
}

.error-preview {
  background-color: var(--va-danger-opacity-20, rgba(211, 47, 47, 0.1));
  border-left: 4px solid var(--va-danger);
  color: var(--va-danger);
}

.anonymous-section {
  display: flex;
  align-items: center;
}

/* Adjustments for input labels if we keep them */
/* :deep(.direct-id-input .va-input-wrapper__label) { */
/* font-weight: 500; */
/* } */
</style>
